import { ITransaction } from '../../../interface/transaction';
import InformationPopup from '../../informationPopup/informationPopup';
import { IShowDialogProps } from '../../../interface/transactionRestriction';
import { useHandleTransactionRestrictions } from '../../../hooks/useHandleTransactionRestrictions';
import { useFetchTransactionRestrictions } from '../../../hooks/useFetchTransactionRestrictions';
import { IMatter } from '../../../interface/matter';
import { useTranslation } from 'react-i18next';
import { Spin } from '../../Ant';

const TransactionRestrictionsContent = ({
  transaction,
  onSelectTransaction,
}: {
  transaction: ITransaction;
  onSelectTransaction?: (transaction?: ITransaction) => void;
}) => {
  const { t } = useTranslation();
  const { data: matterTransactionRestrictions, isLoading: isLoadingTransactionRestrictions } =
    useFetchTransactionRestrictions({
      matterId: transaction?.matterId,
      enabled: !!transaction?.matterId,
    });

  const onSelect = (_matter: IMatter) => {
    onSelectTransaction?.(transaction);
  };
  const {
    serviceRestrictionErrorDialog,
    setServiceRestrictionErrorDialog,
    customerIdentificationErrorDialog,
    setCustomerIdentificationErrorDialog,
    handleServiceRestrictionChecks,
  } = useHandleTransactionRestrictions({
    selectedMatter: transaction?.matter,
    matterTransactionRestrictions,
    onSelect,
  });

  // Error level restrictions bar the transaction, so the dialog only acknowledges them: dismissing
  // it drops the pending selection instead of opening the transaction.
  const renderInformationPopup = (
    dialog: IShowDialogProps,
    setDialog: (dialog: IShowDialogProps) => void,
    disableConfirmButton = false,
    options: {
      onClose?: () => void;
      onConfirm?: () => void;
    } = {},
  ) => (
    <InformationPopup
      visible={dialog.visible}
      onConfirm={() => {
        setDialog({ visible: false, message: '' });
        options.onClose?.();
      }}
      confirmButtonType={disableConfirmButton ? undefined : 'primary'}
      cancelButtonType="default"
      title={dialog.message ?? ''}
      subtitle={
        disableConfirmButton
          ? t('transaction:restrictions.youCannotEnterTransactionsForMatter')
          : t('transaction:restrictions.doYouWantToContinue')
      }
      confirmText={disableConfirmButton ? undefined : t('common:continue')}
      cancelText={t('common:cancel')}
      onClose={() => {
        setDialog({ visible: false, message: '' });
        options.onConfirm?.();
      }}
    />
  );

  return (
    <div>
      {renderInformationPopup(
        customerIdentificationErrorDialog,
        setCustomerIdentificationErrorDialog,
        false,
        {
          onClose: () => {
            handleServiceRestrictionChecks(matterTransactionRestrictions, true, transaction.matter);
          },
          onConfirm: () => {
            onSelectTransaction?.();
          },
        },
      )}
      {renderInformationPopup(
        serviceRestrictionErrorDialog,
        setServiceRestrictionErrorDialog,
        false,
        {
          onClose: () => {
            onSelect(transaction.matter);
          },
          onConfirm: () => {
            onSelectTransaction?.();
          },
        },
      )}
      {isLoadingTransactionRestrictions && <Spin fullscreen data-testid="loading-spinner" />}
    </div>
  );
};

const RenderTransactionRestrictions = ({
  transaction,
  onSelectTransaction,
}: {
  transaction?: ITransaction;
  onSelectTransaction?: (transaction?: ITransaction) => void;
}) => {
  if (!transaction) return null;
  return (
    <TransactionRestrictionsContent
      transaction={transaction}
      onSelectTransaction={onSelectTransaction}
    />
  );
};

export default RenderTransactionRestrictions;
